import React from 'react';
import { BrainCircuit, AlertTriangle, Lightbulb, BarChart2, Plus, MessageSquare, FileUp, GripVertical, PieChart as PieChartIcon } from 'lucide-react';
import { Insight } from '../types';

const Panel: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className }) => (
    <div className={`glass-panel border border-white/10 rounded-2xl shadow-xl ${className}`}>
        {children}
    </div>
);

const InsightActionButton: React.FC<{ icon: React.ReactElement<{ className?: string }>, label: string, onClick: () => void }> = ({ icon, label, onClick }) => (
    <button
        onClick={onClick}
        className="flex items-center text-xs font-bold text-neon-gold bg-white/5 hover:bg-neon-gold hover:text-white border border-white/10 px-2.5 py-1.5 rounded-md transition-all duration-200 shadow-sm hover:shadow-[0_0_10px_rgba(245,158,11,0.3)]"
    >
        {React.cloneElement(icon, { className: 'h-3.5 w-3.5 mr-1.5' })}
        {label}
    </button>
);

export const InsightCard: React.FC<{
    insight: Insight;
    onGenerateChartFromInsight: (insight: Insight) => void;
    onAddChartToDashboardFromInsight: (insight: Insight) => void;
    onExplainInsight: (insight: Insight) => void;
    onCreateStoryFromInsight: (insight: Insight) => void;
}> = ({ insight, onGenerateChartFromInsight, onAddChartToDashboardFromInsight, onExplainInsight, onCreateStoryFromInsight }) => {
    const getIcon = () => {
        switch (insight.type) {
            case 'anomaly': return <AlertTriangle className="text-amber-500 w-5 h-5" />;
            case 'correlation': return <GripVertical className="text-emerald-500 w-5 h-5" />;
            case 'distribution': return <PieChartIcon className="text-sky-500 w-5 h-5" />;
            default: return <Lightbulb className="text-violet-500 w-5 h-5" />;
        }
    };
    return (
        <Panel className="p-5 flex flex-col justify-between hover:border-neon-gold/30 transition-all duration-300 group">
            <div>
                <div className="flex items-start mb-2 gap-3">
                    <div className="p-2 bg-white/5 rounded-full flex-shrink-0 border border-white/10 group-hover:border-neon-gold/30 transition-colors">{getIcon()}</div>
                    <h4 className="font-bold text-gray-200 text-base leading-tight mt-1 group-hover:text-neon-gold transition-colors">{insight.title}</h4>
                </div>
                <p className="text-white/80 text-sm leading-relaxed">{insight.summary}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-2">
                <InsightActionButton icon={<BarChart2 />} label="Chart" onClick={() => onGenerateChartFromInsight(insight)} />
                <InsightActionButton icon={<Plus />} label="To Dash" onClick={() => onAddChartToDashboardFromInsight(insight)} />
                <InsightActionButton icon={<MessageSquare />} label="Explain" onClick={() => onExplainInsight(insight)} />
                <InsightActionButton icon={<FileUp />} label="Story" onClick={() => onCreateStoryFromInsight(insight)} />
            </div>
        </Panel>
    );
};

const InsightsPage: React.FC<{
    insights: Insight[];
    isLoading: boolean;
    error: string | null;
    onGenerate: () => void;
    onGenerateChart: (insight: Insight) => void;
    onAddToDashboard: (insight: Insight) => void;
    onExplain: (insight: Insight) => void;
    onCreateStory: (insight: Insight) => void;
}> = ({ insights, isLoading, error, onGenerate, onGenerateChart, onAddToDashboard, onExplain, onCreateStory }) => {
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-neon-gold">
                <BrainCircuit className="w-12 h-12 mb-4 animate-pulse" />
                <p className="text-lg font-medium">Analyzing data patterns...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-red-400">
                <AlertTriangle className="w-12 h-12 mb-4" />
                <p className="text-lg font-medium">{error}</p>
                <button onClick={onGenerate} className="mt-4 px-4 py-2 bg-red-900/20 border border-red-500/30 rounded-lg hover:bg-red-900/40 transition-colors">
                    Try Again
                </button>
            </div>
        );
    }

    if (insights.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                <Lightbulb className="w-16 h-16 mb-4 opacity-20" />
                <p className="text-lg font-medium mb-2">No insights generated yet</p>
                <p className="text-sm mb-6 max-w-md text-center">AI can analyze your data to find anomalies, trends, and correlations automatically.</p>
                <button
                    onClick={onGenerate}
                    className="px-6 py-3 bg-neon-gold hover:bg-neon-gold/80 text-white font-bold rounded-lg shadow-[0_0_20px_rgba(245,158,11,0.3)] transition-all flex items-center"
                >
                    <BrainCircuit className="w-5 h-5 mr-2" />
                    Generate Insights
                </button>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {insights.map((insight, idx) => (
                <InsightCard
                    key={idx}
                    insight={insight}
                    onGenerateChartFromInsight={onGenerateChart}
                    onAddChartToDashboardFromInsight={onAddToDashboard}
                    onExplainInsight={onExplain}
                    onCreateStoryFromInsight={onCreateStory}
                />
            ))}
        </div>
    );
};

export default InsightsPage;
